import * as React from "react";
import { ulid } from "ulid";
import { SidePanel as SharedSidePanel } from "@/components/chat/SidePanel";
import type { SidePanelTab, SidePanelTabConfig } from "@/components/chat/types";
import { SidebarPromptImprovements } from "./sidebar/sidebarPromptImprovements";
import { SidebarHistory } from "./sidebar/sidebarHistory";
import { SidebarSources } from "./sidebar/sidebarSources";
import { PromptLibraryRightDrawer } from "@/modules/promptLibrary/components/promptLibraryRightDrawer";
import { PanelType } from "../types";
import type { OpenChatMessage } from "@/types/open-chat";

interface Props {
  activePanel: PanelType;
  isPanelOpen: boolean;
  onPanelChange: (panel: Exclude<PanelType, null>) => void;
  onClose: () => void;
  conversationId: string;
  messages: OpenChatMessage[];
  activeSourceIds?: number[];
  activeMessageId?: string | null;
}

const PANEL_TO_TAB: Record<Exclude<PanelType, null>, SidePanelTab> = {
  improve: "improve",
  library: "library",
  history: "history",
  sources: "sources",
};

const TAB_TO_PANEL: Record<SidePanelTab, PanelType> = {
  pdf: null,
  artefacts: null,
  improve: "improve",
  library: "library",
  history: "history",
  sources: "sources",
  files: null,
  snapshot: null,
};

export const SidePanel = ({
  activePanel,
  isPanelOpen,
  onPanelChange,
  onClose,
  conversationId,
  messages,
  activeSourceIds,
  activeMessageId,
}: Props) => {
  const [promptGeneratorConversationId] = React.useState(() => ulid());

  const hasSources = messages.some(m => m.searchSources?.sources?.length);

  const tabs: SidePanelTabConfig[] = React.useMemo(
    () => [
      { id: "improve", label: "Improve" },
      { id: "library", label: "Library" },
      { id: "history", label: "History" },
      { id: "sources", label: "Sources", show: hasSources },
    ],
    [hasSources],
  );

  const activeTab = activePanel ? PANEL_TO_TAB[activePanel] : null;

  const handleTabChange = (tab: SidePanelTab) => {
    const panel = TAB_TO_PANEL[tab];
    if (panel) {
      onPanelChange(panel);
    }
  };

  return (
    <SharedSidePanel
      activeTab={activeTab}
      isOpen={isPanelOpen}
      tabs={tabs}
      onTabChange={handleTabChange}
      onClose={onClose}
    >
      {activePanel === "improve" && <SidebarPromptImprovements conversationId={promptGeneratorConversationId} />}
      {activePanel === "library" && <PromptLibraryRightDrawer source="open_chat" />}
      {activePanel === "history" && <SidebarHistory conversationId={conversationId} />}
      {activePanel === "sources" && (
        <SidebarSources messages={messages} activeSourceIds={activeSourceIds} activeMessageId={activeMessageId} />
      )}
    </SharedSidePanel>
  );
};
